import React, { useState } from 'react';
import { observer } from 'mobx-react';
import {
  toFixed,
  zeroPad,
} from '../../utils/numbers';
import { useStores } from '../../hooks/use-stores';
import {
  Portlet,
  PortletActions,
  PortletBody,
  PortletCaption,
  PortletCaptionSubject,
  PortletFooter,
  PortletTitle,
} from '../../components/Bootstrap/Portlet';
import { withTranslation } from 'react-i18next';
import {
  Col,
  FormControl,
  Row,
} from 'react-bootstrap';
import { displayDate } from '../../utils/date';
import { ConstituentType } from '../../constants/MaterialModels';
import {
  RunMode,
  ToleranceMode,
} from '../../constants/Configuration';
import logger from '../../utils/logger';
import { OrderOrigin } from '../../constants/OrderOrigin';
import { isFeatureEnabled } from '../../utils/feature-flags';

const FEATURE_DROP_VIEW = isFeatureEnabled('drop_view');

const calculatePercentageVariance = (actual, target) => {
  if (!target) return 0;
  return (100 * (actual - target)) / (target || 1);
};

const TicketDetails = observer((props) => {
  const { t } = props;
  const [ codeVisible, setCodeVisible ] = useState(false);
  const [ printing, setPrinting ] = useState(false);
  const [ dropsView, setDropsView ] = useState(false);
  const { ticket } = props;
  const { ticketStore, newAuthStore, printStore, settingsStore, pacStore, locationsStore, materialStore } = useStores();
  const { templateId, templates, timezone } = ticketStore;
  const { configuration } = settingsStore;
  const { printers } = pacStore;
  const numberOfCopies = printStore.isAutoPrint() ? ticketStore.numberOfCopies : 1;
  const printer = pacStore.printer || configuration.printer;

  const printTicket = () => {
    if (!ticketStore.templateId) {
      let css = '.logo img { max-width: 80px; max-height: 80px; } .drop-view-switch { display: none; }';
      let logo = document.querySelector('.navbar-brand').innerHTML;
      logo = `<span class="logo">${logo}</span>`;
      let html = document.querySelector('.ticket-details .tickets-item').innerHTML;
      let ticketToPrint = `
        ${logo}
        <h3 style="float: right;">
            ${ticket.runMode && ticket.runMode !== RunMode.Normal ? t('Duplicate') : ''}
        </h3>
        <h2>
          ${t('Ticket')} ${ticket.dispatchTicketCode || ticket.id}
          ${ticket.isDemo ? t('Demo') : ''}
          ${ticket.isTestMode ? t('Test') : ''}
          ${ticket.runMode && ticket.runMode !== RunMode.Normal ? ticket.runMode : ''}
        </h2>
        ${html}
      `;

      ticketToPrint = printStore.getTicketCopies(ticketToPrint, numberOfCopies);
      printStore.printHtml(ticketToPrint, css);

      return;
    }

    setPrinting(true);

    ticketStore.getTemplate().finally(() => {
      printStore.printTemplate({ ...ticket, isDuplicate: true }, numberOfCopies, printer)
        .catch(e => logger.warn(e))
        .finally(() => {
          setPrinting(false);
        });
    });
  };

  const toggleCode = () => {
    setCodeVisible(!codeVisible);
  };

  const onTemplateChange = (ticketTemplateId) => {
    ticketStore.templateId = ticketTemplateId;
    newAuthStore.storage.setItem('ticketTemplateId', ticketTemplateId);
  };

  const onNumberOfCopiesChange = (numberOfCopies = 1) => {
    ticketStore.numberOfCopies = numberOfCopies;
  };

  const onPrinterChange = (printer) => {
    pacStore.printer = printer;
  };

  const renderDrops = () => {
    return Object.keys(ticket.batchDrops).length > 1
      && (
        <div className="row batch-drops">
          {Object.keys(ticket.batchDrops).map((dropNumber) => (
            <div key={dropNumber} className="col-sm-12">
              <h4>{t('Drop')} {dropNumber}</h4>
              {ticket.dropTimes?.length > 0 && (
                <div className="row">
                  <div className="col-sm-4">
                    <strong>{t('Start')}: </strong>
                    <span data-e2e-id={`lbl-ticket-drop-${dropNumber}-end-value`}>
                      {ticket.dropTimesMap[dropNumber]?.loadStartTime}
                    </span>
                  </div>
                  <div className="col-sm-4">
                    <strong>{t('End')}: </strong>
                    <span data-e2e-id={`lbl-ticket-drop-${dropNumber}-start-value`}>
                      {ticket.dropTimesMap[dropNumber]?.loadEndTime}
                    </span>
                  </div>
                  <div className="col-sm-4">
                    <strong>{t('Time')}: </strong>
                    <span data-e2e-id={`lbl-ticket-drop-${dropNumber}-time-value`}>
                      {ticket.dropTimesMap[dropNumber]?.loadTime}
                    </span>
                  </div>
                </div>
              )}
              {renderWeights(ticket.batchDrops[dropNumber])}
            </div>
          ))}
        </div>
      );
  };

  const renderWeights = (weights) => {
    return (
      <table className="table table-striped table-ticket-weights">
        <thead>
          <tr>
            <th data-e2e-id="tbl-ticket-material-code-header">{t('Material')}</th>
            <th data-e2e-id="tbl-ticket-material-design-header">{t('Design')}</th>
            <th data-e2e-id="tbl-ticket-material-absorption-header">% {t('Absorption')}</th>
            <th data-e2e-id="tbl-ticket-material-moisture-header">% {t('Moisture')}</th>
            <th data-e2e-id="tbl-ticket-material-mcfac-header">{t('MCFAC')}</th>
            <th data-e2e-id="tbl-ticket-material-trim-header">{t('Trim')}</th>
            {configuration.toleranceMode === ToleranceMode.NYDOTCumulative && (
              <>
                <th data-e2e-id="tbl-ticket-material-low-header">{t('Low')}</th>
                <th data-e2e-id="tbl-ticket-material-high-header">{t('High')}</th>
                <th data-e2e-id="tbl-ticket-material-start-tare-header">{t('Start Tare')}</th>
                <th data-e2e-id="tbl-ticket-material-disch-tare-header">{t('Disch Tare')}</th>
              </>
            )}
            <th data-e2e-id="tbl-ticket-material-target-header">{t('Target')}</th>
            <th data-e2e-id="tbl-ticket-material-actual-header">{t('Actual')}</th>
            <th data-e2e-id="tbl-ticket-material-var-header">{t('VAR')}</th>
            <th data-e2e-id="tbl-ticket-material-var-percent-header">% {t('VAR')}</th>
            <th data-e2e-id="tbl-ticket-material-water-header">{t('Water')}</th>
            <th data-e2e-id="tbl-ticket-material-water-free-header">{t('Water Free')}</th>
          </tr>
        </thead>

        <tbody>

          {weights.map((v, i) => {
            const material = materialStore.getMaterialByItemId(v.itemId);
            const md = material ? materialStore.materialDetails[material.materialDetailId] : {};
            // If material's graduation or meter rate is > 1, we should pad zeros
            // as per NTEP certification requirements. (00, 06 instead of 1, 6 etc.)
            const shouldPadZeros = (
              materialStore.getScaleGraduationForConstituent(material) > 1
              || md.meterRate > 1
            );
            return (
              <tr key={i}>
                <td data-e2e-id={`tbl-ticket-material-code-${v.displayName}`}>
                  <strong>
                    {v.isManuallyAdded && '+'}
                    {v.displayName || v.name}
                  </strong>
                </td>
                <td data-e2e-id={`tbl-ticket-material-design-${v.displayName}`}>{v.mix} {ticketStore.getUnitSymbol(v.unitId)}</td>
                <td data-e2e-id={`tbl-ticket-material-absorption-${v.displayName}`}>{toFixed((v.absorption || 0))} %</td>
                <td data-e2e-id={`tbl-ticket-material-moisture-${v.displayName}`}>{toFixed((v.moisture || 0))} %</td>
                <td data-e2e-id={`tbl-ticket-material-mcfac-${v.displayName}`}>{toFixed((v.moisture || ticketStore.getWaterPercentForResultItem(v) || 0)/100, 4)}</td>
                <td data-e2e-id={`tbl-ticket-material-trim-${v.displayName}`}>{toFixed(v.trim)}</td>
                {configuration.toleranceMode === ToleranceMode.NYDOTCumulative && (
                  <React.Fragment>
                    <td data-e2e-id={`tbl-ticket-material-low-${v.displayName}`}>{toFixed(v.low)} {ticketStore.getUnitSymbol(v.unitId)}</td>
                    <td data-e2e-id={`tbl-ticket-material-high-${v.displayName}`}>{toFixed(v.high)} {ticketStore.getUnitSymbol(v.unitId)}</td>
                    <td data-e2e-id={`tbl-ticket-material-start-tare-${v.displayName}`}>{v.startTare}</td>
                    <td data-e2e-id={`tbl-ticket-material-disch-tare-${v.displayName}`}>{v.dischTare}</td>
                  </React.Fragment>
                )}
                <td data-e2e-id={`tbl-ticket-material-target-${v.displayName}`}>{shouldPadZeros ? zeroPad(v.target, 2) : v.target} {ticketStore.getUnitSymbol(v.unitId)}</td>
                <td data-e2e-id={`tbl-ticket-material-actual-${v.displayName}`}>{v.hasIndicatorError ? t('ERROR') : <>{v.infoSigns} {shouldPadZeros ? zeroPad(v.actual, 2) : v.actual} {ticketStore.getUnitSymbol(v.unitId)}</>}</td>
                <td data-e2e-id={`tbl-ticket-material-var-${v.displayName}`}>{v.hasIndicatorError ? t('ERROR') : <>{toFixed(v.actual - v.target, 0)} {ticketStore.getUnitSymbol(v.unitId)}</>}</td>
                <td data-e2e-id={`tbl-ticket-material-var-percent-${v.displayName}`}>{v.hasIndicatorError ? t('ERROR') : <>{toFixed(calculatePercentageVariance(v.actual, v.target))} %</>}</td>
                <td data-e2e-id={`tbl-ticket-material-water-${v.displayName}`}>
                  {(v.waterPercent || v.moisture || v.type === ConstituentType.WATER) && v.actual
                    ? <span>{toFixed(ticketStore.getActualWater(v))} {ticket.waterUnit}</span>
                    : ''
                  }
                </td>
                <td data-e2e-id={`tbl-ticket-material-free-water-${v.displayName}`}>
                  {(v.waterPercent || v.moisture || v.type === ConstituentType.WATER) && v.actual
                    ? <span>{toFixed(ticketStore.getActualFreeWater(v), 0)} {ticket.unit}</span>
                    : ''
                  }
                </td>
              </tr>
            );
          })}

        </tbody>

      </table>
    );
  };

  return(
    <Portlet className="ticket-details">
      <PortletTitle>
        <PortletCaption icon={'fa-file-text-o'}>
          <PortletCaptionSubject>{t('Ticket')} {configuration.useTicketNumber ? ticket.number : ticket.dispatchTicketCode
            ? ticket.dispatchTicketCode
            : ticket.id} {ticket.isDemo ? <span className={'ticket-demo'}>{t('Demo')}</span> : ''}
          {ticket.isTestMode ? <span className={'ticket-test'}> {t('Test')}</span> : ''}
          {ticket.runMode && ticket.runMode !== RunMode.Normal ? <span className={'ticket-mode'}> {ticket.runMode}</span> : ''}
          {ticket.isVoided ? <span className={'ticket-voided'}> {t('Voided')}</span> : ''}
          {ticket.isOffline ? <span className={'ticket-demo'}> {t('Offline not synced')}</span> : ''}
          </PortletCaptionSubject>
        </PortletCaption>
        {printing ? <PortletActions><i className="fa fa-print"/> {t('Printing')}...</PortletActions> : (
          <PortletActions>
            <span>{t('Template')}:</span>
            <FormControl
              name="dischargeSequenceId"
              className="template-select"
              componentClass="select"
              value={templateId || ''}
              onChange={e => onTemplateChange(e.target.value)}
              bsSize="sm"
            >
              <option value="">{t('Choose template')}</option>
              {templates.map(option => (
                <option key={option.id} value={option.id}>
                  {option.description}
                </option>
              ))}
            </FormControl>

            {printStore.isAutoPrint() && (
              <React.Fragment>
                <span> {t('Number of copies')}:</span>
                <FormControl
                  name="numberOfCopies"
                  className="number-copies"
                  type="number"
                  min={1}
                  max={10}
                  value={numberOfCopies}
                  onChange={e => onNumberOfCopiesChange(Number(e.target.value))}
                  bsSize="sm"
                />
                <span>{t('Printer')}:</span>
                <FormControl
                  name="printer"
                  className="template-select"
                  componentClass="select"
                  value={printer || ''}
                  onChange={e => onPrinterChange(e.target.value)}
                  bsSize="sm"
                >
                  <option value="">{t('Choose printer')}</option>
                  {printers.map(p => (
                    <option key={p.deviceId} value={p.name}>
                      {p.name}
                    </option>
                  ))}
                </FormControl>
              </React.Fragment>
            )}

            <a className="btn btn-sm btn-simple hidden-xs" onClick={() => printTicket()}>
              <i className="fa fa-print"/> {t('Print')}
            </a>
          </PortletActions>
        )}
      </PortletTitle>
      <PortletBody>
        <div className="tickets-item">
          <div className="ticket-data">
            <div className="row">
              <div className="col-sm-3">
                <span className="ticket-field"><strong className="ticket-field-label" data-e2e-id="lbl-ticket-date">{t('Date')}: </strong>
                  <span data-e2e-id="lbl-ticket-date-value">{displayDate(ticket.loadStartTime, timezone, 'L', newAuthStore.user?.language)}</span>
                </span>
              </div>
              <div className="col-sm-3">
                <span className="ticket-field"><strong className="ticket-field-label" data-e2e-id="lbl-ticket-plant">{t('Plant')}: </strong>
                  <span data-e2e-id="lbl-ticket-plant-value">{locationsStore.currentPlant.description}</span>
                </span>
              </div>
              <div className="col-sm-3">
                <span className="ticket-field"><strong className="ticket-field-label" data-e2e-id="lbl-ticket-truck">{t('Truck')}: </strong>
                  <span data-e2e-id="lbl-ticket-truck-value">{ticket.truckCode}</span>
                </span>
              </div>
              <div className="col-sm-3">
                <span className="ticket-field"><strong className="ticket-field-label" data-e2e-id="lbl-ticket-driver">{t('Driver')}: </strong>
                  <span data-e2e-id="lbl-ticket-driver-value">{ticket.driverName}</span>
                </span>
              </div>
            </div>

            <div className="row">
              <div className="col-sm-3">
                <span className="ticket-field"><strong className="ticket-field-label" data-e2e-id="lbl-ticket-order-code">{t('Order Code')}: </strong>
                  <span data-e2e-id="lbl-ticket-order-code-value">{ticket.dispatchOrderCode || ticket.batchOrderCode || ticket.batchOrderId || ticket.code}</span>
                </span>
              </div>
              <div className="col-sm-3">
                <span className="ticket-field"><strong className="ticket-field-label" data-e2e-id="lbl-ticket-po">{t('PO')}: </strong>
                  <span data-e2e-id="lbl-ticket-po-value">{ticket.purchaseOrderNumber}</span>
                </span>
              </div>
              <div className="col-sm-3">
                <span className="ticket-field"><strong className="ticket-field-label" data-e2e-id="lbl-ticket-payment-form">{t('Payment Form')}: </strong>
                  <span data-e2e-id="lbl-ticket-payment-form-value">{ticket.paymentForm}</span>
                </span>
              </div>
              <div className="col-sm-3">
                <span className="ticket-field"><strong className="ticket-field-label" data-e2e-id="lbl-ticket-zone-code">{t('Zone Code')}: </strong>
                  <span data-e2e-id="lbl-ticket-zone-code-value">{ticket.zoneCode}</span>
                </span>
              </div>
              <div className="col-sm-3">
                <span className="ticket-field"><strong className="ticket-field-label" data-e2e-id="lbl-ticket-usage">{t('Usage')}: </strong>
                  <span data-e2e-id="lbl-ticket-usage-value">{ticket.usage}</span>
                </span>
              </div>
              <div className="col-sm-3">
                <span className="ticket-field"><strong className="ticket-field-label" data-e2e-id="lbl-ticket-customer-code">{t('Customer')}#: </strong>
                  <span data-e2e-id="lbl-ticket-customer-code-value">{ticket.customerCode}</span>
                </span>
              </div>
              <div className="col-sm-3">
                <span className="ticket-field"><strong className="ticket-field-label" data-e2e-id="lbl-ticket-customer-name">{t('Customer')}: </strong>
                  <span data-e2e-id="lbl-ticket-customer-name-value">{ticket.customerName}</span>
                </span>
              </div>
              <div className="col-sm-3">
                <span className="ticket-field"><strong className="ticket-field-label" data-e2e-id="lbl-ticket-lot-block">{t('Lot/Block')}: </strong>
                  <span data-e2e-id="lbl-ticket-lot-block-value">{ticket.lotBlockNumber}</span>
                </span>
              </div>
            </div>
            <div className="row">
              <div className="col-sm-6">
                <span className="ticket-field"><strong className="ticket-field-label" data-e2e-id="lbl-ticket-project-code">{t('Project')}#: </strong>
                  <span data-e2e-id="lbl-ticket-project-code-value">{ticket.projectCode}</span>
                </span>
              </div>
              <div className="col-sm-6">
                <span className="ticket-field"><strong className="ticket-field-label" data-e2e-id="lbl-ticket-project-name">{t('Project')}: </strong>
                  <span data-e2e-id="lbl-ticket-project-name-value">{ticket.projectName}</span>
                </span>
              </div>
            </div>
            <div className="row">
              <div className="col-sm-12">
                <span className="ticket-field"><strong className="ticket-field-label" data-e2e-id="lbl-ticket-sold-to">{t('Sold to')}: </strong>
                  <span data-e2e-id="lbl-ticket-sold-to-value">{ticket.customerAddress}</span>
                </span>
              </div>
            </div>
            <div className="row">
              <div className="col-sm-12">
                <span className="ticket-field"><strong className="ticket-field-label" data-e2e-id="lbl-ticket-delivered-to">{t('Delivered to')}: </strong>
                  <span data-e2e-id="lbl-ticket-delivered-to-value">{ticket.shipToAddress}</span>
                </span>
              </div>
            </div>
          </div>

          <table className="table table-striped table-ticket-products">
            <thead>
              <tr>
                <th data-e2e-id="tbl-ticket-mix-code-header">{t('Product#')}</th>
                <th data-e2e-id="tbl-ticket-mix-description-header">{t('Description')}</th>
                <th data-e2e-id="tbl-ticket-mix-quantity-header">{t('Qty')}</th>
                <th data-e2e-id="tbl-ticket-mix-unit-header">{t('Unit')}</th>
                <th data-e2e-id="tbl-ticket-mix-unit-price-header">{t('Unit price')}</th>
                <th className={'text-right'} data-e2e-id="tbl-ticket-mix-amount-header">{t('Amount')}</th>
              </tr>
            </thead>

            <tbody>

              {ticket.products.map((v, i) => (
                <tr key={i}>
                  <td data-e2e-id={`tbl-ticket-mix-code-${v.itemCode}`}>{v.itemCode}</td>
                  <td data-e2e-id={`tbl-ticket-mix-description-${v.itemCode}`}>{v.itemDescription}</td>
                  <td data-e2e-id={`tbl-ticket-mix-quantity-${v.itemCode}`}>{v.quantity}</td>
                  <td data-e2e-id={`tbl-ticket-mix-unit-${v.itemCode}`}>{v.unitSymbol}</td>
                  <td data-e2e-id={`tbl-ticket-mix-unit-price-${v.itemCode}`}>{ticket.printPrices ? `$${toFixed(v.unitPrice)}` : ''}</td>
                  <td className={'text-right'} data-e2e-id={`tbl-ticket-mix-amount-${v.itemCode}`}>
                    {ticket.printPrices && (
                      <span>
                        {ticket.origin === OrderOrigin.External && v.totalQuantityPrice
                          ? `$${toFixed(v.totalQuantityPrice)}`
                          : `$${toFixed((v.unitPrice * v.quantity))}`
                        }
                      </span>
                    )}
                  </td>
                </tr>
              ))}

              <tr className="row-ticket-products-total">
                <td colSpan="4" rowSpan="4"/>
                <td><strong data-e2e-id="lbl-ticket-sub-total">{t('Sub Total')}: </strong></td>
                <td className={'text-right'} data-e2e-id="lbl-ticket-sub-total-value">{ticket.printPrices ? `$${toFixed(ticket.ticketSubTotal)}` : ''}</td>
              </tr>
              <tr className="row-ticket-products-total">
                <td><strong data-e2e-id="lbl-ticket-tax">{t('TAX')}: </strong></td>
                <td className={'text-right'} data-e2e-id="lbl-ticket-tax-value">{ticket.printPrices ? `$${toFixed(ticket.ticketTaxTotal)}` : ''}</td>
              </tr>
              <tr className="row-ticket-products-total">
                <td><strong data-e2e-id="lbl-ticket-total">{t('Total')}:</strong></td>
                <td className={'text-right'} data-e2e-id="lbl-ticket-total-value">{ticket.printPrices ? `$${toFixed(ticket.ticketTotal)}` : ''}
                </td>
              </tr>
              <tr className="row-ticket-products-total">
                <td><strong data-e2e-id="lbl-ticket-grand-total">{t('Grand Total')}:</strong></td>
                <td className={'text-right'} data-e2e-id="lbl-ticket-grand-total-value">{ticket.printPrices ? `$${toFixed(ticket.orderDeliveredToDatePriceWithTax)}` : ''}
                </td>
              </tr>
            </tbody>

            <tfoot>
              <tr>
                <td colSpan="6">
                  <span className="ticket-field"><strong className="ticket-field-label" data-e2e-id="lbl-ticket-ordered">{t('Ordered')}: </strong>
                    <span data-e2e-id="lbl-ticket-ordered-value">
                      {ticket.orderMixQuantity}{' '}
                    </span>
                  </span>
                  <span className="ticket-field"><strong className="ticket-field-label" data-e2e-id="lbl-ticket-delivered">{t('Delivered')}: </strong>
                    <span data-e2e-id="lbl-ticket-delivered-value">
                      {ticket.orderDeliveredToDateMixQuantity}{' '}
                    </span>
                  </span>
                  <span className="ticket-field"><strong className="ticket-field-label" data-e2e-id="lbl-ticket-slump">{t('Slump')}: </strong>
                    <span data-e2e-id="lbl-ticket-slump-value">
                      {ticket.mixSlump}{' '}
                    </span>
                  </span>
                </td>
              </tr>
            </tfoot>

          </table>

          <div className="row">

            {ticket.instructions && !!ticket.instructions.length && (
              <div className="col-sm-12">
                <div className="ticket-field margin-bottom-10">
                  <strong className="ticket-field-label">{t('Instructions')}: </strong>
                  {ticket.instructions.map((instruction, i) => (
                    <div key={i}>{instruction}</div>
                  ))}
                </div>
              </div>
            )}

            <div className="col-sm-4">
              <span className="ticket-field">
                <strong className="ticket-field-label" data-e2e-id="lbl-ticket-batch-start">{t('Batch start')}: </strong>
                <span data-e2e-id="lbl-ticket-batch-start-value">{displayDate(ticket.loadStartTime, timezone, 'LTS', newAuthStore.user?.language)}</span>
              </span>
            </div>
            <div className="col-sm-4">
              <span className="ticket-field">
                <strong className="ticket-field-label" data-e2e-id="lbl-ticket-batch-end">{t('Batch end')}: </strong>
                <span data-e2e-id="lbl-ticket-batch-end-value">{ticket.loadEndTime ? displayDate(ticket.loadEndTime, timezone, 'LTS', newAuthStore.user?.language) : ''}</span>
              </span>
            </div>
            <div className="col-sm-4">
              <span className="ticket-field">
                <strong className="ticket-field-label" data-e2e-id="lbl-ticket-batch-time">{t('Batch time')}:</strong>
                <span data-e2e-id="lbl-ticket-batch-time-value">{ticketStore.getBatchTime(ticket)}</span>
              </span>
            </div>
            {!!configuration.recordManualBatchToTicket && (
              <div className="col-sm-4">
                <span className="ticket-field">
                  <strong
                    className="ticket-field-label">{ticket.manualLoad ? t('Manual Batch') : t('Automatic Batch')}
                  </strong>
                </span>
              </div>
            )}
            {!!ticket.isAborted && (
              <div className="col-sm-4">
                <span className="ticket-field">
                  <strong className="ticket-field-label" data-e2e-id="lbl-ticket-load-aborted">{t('Load Aborted')}</strong>
                </span>
              </div>
            )}
          </div>

          {FEATURE_DROP_VIEW && Object.keys(ticket.batchDrops).length > 1 && (
            <div className="row drop-view-switch">
              <div className="col-sm-12">
                <strong className="ticket-field-label" data-e2e-id="lbl-ticket-drop-view">{t('Drop View')}{' '}</strong>
                <input
                  data-e2e-id="chb-ticket-drop-view"
                  type="checkbox"
                  name="dropView"
                  checked={dropsView}
                  onChange={(e) => setDropsView(e.target.checked)}
                />
              </div>
            </div>
          )}

          {ticket.results && !!ticket.results.length && (
            <div className="table-ticket-weights-wrap">

              {dropsView && Object.keys(ticket.batchDrops).length > 1
                ? renderDrops()
                : renderWeights(ticket.results)
              }

              <div className="row">
                <div className="col-sm-4">
                  <span className="ticket-field">
                    <strong className="ticket-field-label" data-e2e-id="lbl-ticket-total-load">{t('Total Load')}: </strong>
                    <span data-e2e-id="lbl-ticket-total-load-value">{ticket.totalLoad} {ticket.unit}</span>
                  </span>
                </div>
                <div className="col-sm-4">
                  <span className="ticket-field">
                    <strong className="ticket-field-label" data-e2e-id="lbl-ticket-design-wc-ratio">{t('Design W/C Ratio')}: </strong>
                    <span data-e2e-id="lbl-ticket-design-wc-ratio-value">{toFixed(ticket.designWaterCementRatio, 3)}</span>
                  </span>
                </div>
                <div className="col-sm-4">
                  <span className="ticket-field">
                    <strong className="ticket-field-label" data-e2e-id="lbl-ticket-wc-ratio">{t('W/C Ratio')}: </strong>
                    <span data-e2e-id="lbl-ticket-wc-ratio-value">{toFixed(ticket.waterCementRatio, 3)}</span>
                  </span>
                </div>
                <div className="col-sm-4">
                  <span className="ticket-field">
                    <strong className="ticket-field-label" data-e2e-id="lbl-ticket-water-in-truck">{t('Water in Truck')}: </strong>
                    <span data-e2e-id="lbl-ticket-water-in-truck-value">{toFixed(ticket.waterInTruck || 0)} {ticket.waterUnit}</span>
                  </span>
                </div>
                <div className="col-sm-4">
                  <span className="ticket-field">
                    <strong className="ticket-field-label" data-e2e-id="lbl-ticket-ice">{t('Ice')}: </strong>
                    <span data-e2e-id="lbl-ticket-ice-value">{ticket.ice || 0} {ticket.iceUnit}</span>
                  </span>
                </div>
                <div className="col-sm-4">
                  <span className="ticket-field">
                    <strong className="ticket-field-label" data-e2e-id="lbl-ticket-total-water">{t('Total Water')}: </strong>
                    <span data-e2e-id="lbl-ticket-total-water-value">{toFixed(ticket.totalWater)} {ticket.waterUnit}</span>
                  </span>
                </div>
                <div className="col-sm-4">
                  <span className="ticket-field">
                    <strong className="ticket-field-label" data-e2e-id="lbl-ticket-total-water-free">{t('Total Water Free')}: </strong>
                    <span data-e2e-id="lbl-ticket-total-water-free-value">{ticket.totalFreeWater} {ticket.unit}</span>
                  </span>
                </div>
                <div className="col-sm-4">
                  <span className="ticket-field">
                    <strong className="ticket-field-label" data-e2e-id="lbl-ticket-temper-water">{t('Temper Water')}: </strong>
                    <span data-e2e-id="lbl-ticket-temper-water-value">{toFixed(ticket.temperWater || 0)} {ticket.temperWaterUnitSymbol}</span>
                  </span>
                </div>
                <div className="col-sm-4">
                  <span className="ticket-field">
                    <strong className="ticket-field-label" data-e2e-id="lbl-ticket-adjusted-water">{t('Adjusted Water')}: </strong>
                    <span data-e2e-id="lbl-ticket-adjusted-water-value">{toFixed(ticket.adjustedWater || 0)} {ticket.waterUnit}</span>
                  </span>
                </div>
                <div className="col-sm-4">
                  <span className="ticket-field">
                    <strong className="ticket-field-label" data-e2e-id="lbl-ticket-water-allowed-by-design">{t('Water Allowed By Design')}: </strong>
                    <span data-e2e-id="lbl-ticket-water-allowed-by-design-value">{ticket.waterAllowedByDesign} {ticket.unit}</span>
                  </span>
                </div>
                <div className="col-sm-4">
                  <span className="ticket-field">
                    <strong className="ticket-field-label" data-e2e-id="lbl-ticket-water-allowed-to-add">{t('Water Allowed To Add')}: </strong>
                    <span data-e2e-id="lbl-ticket-water-allowed-to-add-value">{toFixed(ticket.waterAllowedToAdd)} {ticket.waterUnit}</span>
                  </span>
                </div>
                {!!ticket.maxWater && !!ticket.waterAllowedToAddByMix && (
                  <div className="col-sm-4">
                    <span className="ticket-field">
                      <strong className="ticket-field-label" data-e2e-id="lbl-ticket-water-allowed-to-add-by-mix">{t('Water Allowed To Add By Mix')}: </strong>
                      <span data-e2e-id="lbl-ticket-water-allowed-to-add-by-mix-value">{toFixed(ticket.waterAllowedToAddByMix, 2)} {ticket.waterUnit}</span>
                    </span>
                  </div>
                )}
                <div className="col-sm-4">
                  <span className="ticket-field">
                    <strong className="ticket-field-label" data-e2e-id="lbl-ticket-total-cement">{t('Total Cement')}: </strong>
                    <span data-e2e-id="lbl-ticket-total-cement-value">{ticket.totalCement} {ticket.unit}</span>
                  </span>
                </div>
                {ticket.mixingTime && (
                  <div className="col-sm-4">
                    <span className="ticket-field">
                      <strong className="ticket-field-label" data-e2e-id="lbl-ticket-mixing-time">{t('Mixing Time')}: </strong>
                      <span data-e2e-id="lbl-ticket-mixing-time-value">{ticket.mixingTime} {t('sec')}</span>
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
          {!!ticket.manualTickets && !!ticket.manualTickets.length && (
            <Row>
              <Col sm={12}>
                <strong className="ticket-field-label">{t('Manual')}: </strong>
              </Col>
              {ticket.manualTickets.map((v, i) => (
                <Col sm={12} key={i}><strong className="ticket-field-label">{v.itemCode}</strong> {v.quantity} {v.unitSymbol}</Col>
              ))}
            </Row>
          )}
          <Row>
            <Col sm={12} className="margin-top-10">
              <div data-e2e-id="lbl-ticket-over-below-target">* - {t('over/below target')}</div>
              <div data-e2e-id="lbl-ticket-manually-terminated">^ - {t('manually terminated')}</div>
              <div data-e2e-id="lbl-ticket-added-manually">+ - {t('added manually')}</div>
            </Col>
          </Row>
          {!!ticket.events?.length && (
            <Row className="margin-top-10">
              <Col sm={12}>
                <strong className="ticket-field-label">{t('Events')}:</strong>
              </Col>
              {ticket.events.map((event, i) => (
                <Col key={`${event.time || i}-${event.eventType || ''}-${event.createdBy || event.author || ''}`} sm={4} className="margin-top-10">
                  <div><strong>{(event.eventType || '').replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase())}</strong></div>
                  <div>{t('Time')}: {displayDate(event.time, timezone, 'LTS', newAuthStore.user?.language)}</div>
                  <div>{t('Initials')}: {event.author}</div>
                  <div>{t('Login')}: {event.createdBy}</div>
                  {event.material && <div>{t('Material')}: {event.material}</div>}
                  {event.output && <div>{t('Output')}: {event.output}</div>}
                </Col>
              ))}
            </Row>
          )}
        </div>

      </PortletBody>
      <PortletFooter>
        <a className="btn btn-sm btn-simple hidden-xs" onClick={toggleCode} data-e2e-id="btn-ticket-toggle-code">
          <i className="fa fa-code"/> {t('Code')}
        </a>

        {codeVisible &&
          <pre>{JSON.stringify(ticket, null, 2)}</pre>
        }
      </PortletFooter>
    </Portlet>
  );
});

export default withTranslation()(TicketDetails);
